import { Component, JSX } from "react";
import { Outlet, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router-dom";
import { oktaAuthConfig } from "../../../configOkta";
import { Security } from "@okta/okta-react";
import Layout from "../../Layout/Layout/Layout";
import NotFound from "../../../Pages/Public/NotFound";
import LegacyRedirect from "./LegacyRedirect";
import RequiresAuth from "./RequiresAuth";
import { ILegacyRoute, IRoute, routes } from "./Routes";
import { appContext } from "../../../AppContext";
import { DirtyPageGuard, DirtyPageProvider } from "@iconics/components";

export default class IbssRouter extends Component<IProps, IState>
{
    private router = this.createRouter();

    private restoreOriginalUri(originalUri: string): void
    {
        window.location.href = window.location.origin + "/auth";
    }

    private createRouter()
    {
        const routeComponents = routes
            .flatMap(route =>
            {
                const routeComponents: JSX.Element[] = [];
                if (route.legacyPaths)
                {
                    const redirects = route.legacyPaths.map(legacyPath => this.renderRedirectRoute(route, legacyPath));
                    routeComponents.push(...redirects);
                }

                if (route.component && route.isPublic)
                {
                    routeComponents.push(this.renderPublicRoute(route));
                }
                else if (route.component)
                {
                    routeComponents.push(this.renderPrivateRoute(route));
                }
                return routeComponents;
            })
            .filter(i => !!i) as JSX.Element[];

        return createBrowserRouter(
            createRoutesFromElements(
                <Route
                    element={
                        <DirtyPageProvider>
                            <DirtyPageGuard message={appContext().labels.Plaftform_Warn_UnsavedChanges} />
                            <Outlet />
                        </DirtyPageProvider>
                    }
                >
                    {routeComponents}
                    <Route
                        path="*"
                        element={<NotFound />}
                    />
                </Route>
            )
        );
    }

    public render(): JSX.Element
    {
        const oktaAuth = oktaAuthConfig;

        return (
            <Security
                oktaAuth={oktaAuth}
                restoreOriginalUri={(_oktaAuth, originalUri) => this.restoreOriginalUri(originalUri)}
            >
                <RouterProvider router={this.router} />
            </Security>
        );
    }

    private renderRedirectRoute(route: IRoute<any>, legacyPath: ILegacyRoute): JSX.Element
    {
        return (
            <Route
                key={legacyPath.path}
                path={legacyPath.path}
                element={<LegacyRedirect
                    route={route}
                    legacyPath={legacyPath}
                />}
            />
        );
    }

    private renderPublicRoute(route: IRoute<any>): JSX.Element
    {
        return (
            <Route
                key={route.path}
                path={route.path}
                element={
                    <Layout isPrivate={false}>
                        <route.component />
                    </Layout>
                }
            />
        );
    }

    private renderPrivateRoute(route: IRoute<any>): JSX.Element
    {
        return (
            <Route
                key={route.path}
                path={route.path}
                element={
                    <RequiresAuth
                        requiredRights={route.requiredRights}
                    >
                        <Layout isPrivate={true}>
                            <route.component {...route.props} />
                        </Layout>
                    </RequiresAuth>
                }
            />
        );
    }
}

export interface IProps { }
export interface IState { }
